import { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import { Database } from '@/integrations/supabase/types';

type Employee = Database['public']['Tables']['employees']['Row'];
type EmployeeSettings = Database['public']['Tables']['employee_settings']['Row'];
type EmployeeNotificationPreferences = Database['public']['Tables']['employee_notification_preferences']['Row'];
type EmployeeAvailabilityPreferences = Database['public']['Tables']['employee_availability_preferences']['Row'];

interface EmployeeContextType {
  // Core employee data
  employee: Employee | null;
  loading: boolean;
  
  // Settings and preferences
  settings: EmployeeSettings[] | null;
  notificationPreferences: EmployeeNotificationPreferences | null;
  availabilityPreferences: EmployeeAvailabilityPreferences | null;
  
  // Setting management functions
  updateSetting: (key: string, value: any, type?: string) => Promise<void>;
  getSetting: (key: string, defaultValue?: any) => any;
  
  // Preference update functions
  updateNotificationPreferences: (preferences: Partial<EmployeeNotificationPreferences>) => Promise<void>;
  updateAvailabilityPreferences: (preferences: Partial<EmployeeAvailabilityPreferences>) => Promise<void>;
  
  // Refresh functions
  refreshEmployeeData: () => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const EmployeeContext = createContext<EmployeeContextType>({
  employee: null,
  loading: true,
  settings: null,
  notificationPreferences: null,
  availabilityPreferences: null,
  updateSetting: async () => {},
  getSetting: () => null,
  updateNotificationPreferences: async () => {},
  updateAvailabilityPreferences: async () => {},
  refreshEmployeeData: async () => {},
  refreshSettings: async () => {},
});

export const EmployeeProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, isEmployee } = useAuth();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<EmployeeSettings[] | null>(null);
  const [notificationPreferences, setNotificationPreferences] = useState<EmployeeNotificationPreferences | null>(null);
  const [availabilityPreferences, setAvailabilityPreferences] = useState<EmployeeAvailabilityPreferences | null>(null);

  // Track if we've already fetched for this user to prevent duplicate fetches
  const fetchedForUserRef = useRef<string | null>(null);

  // Fetch employee data
  const fetchEmployeeData = useCallback(async () => {
    if (!user || !isEmployee) {
      setEmployee(null);
      setSettings(null);
      setNotificationPreferences(null);
      setAvailabilityPreferences(null);
      setLoading(false);
      fetchedForUserRef.current = null;
      return;
    }

    // Skip if we've already fetched for this user (prevents refetch on tab focus)
    if (fetchedForUserRef.current === user.id) {
      return;
    }
    fetchedForUserRef.current = user.id;

    try {
      setLoading(true);

      // Fetch employee record
      const { data: employeeData, error: employeeError } = await supabase
        .from('employees')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (employeeError) {
        console.error('Error fetching employee data:', employeeError);
        setEmployee(null);
        setLoading(false);
        return;
      }

      setEmployee(employeeData);

      // Fetch employee settings in parallel
      const [settingsResult, notificationResult, availabilityResult] = await Promise.allSettled([
        supabase
          .from('employee_settings')
          .select('*')
          .eq('employee_id', employeeData.id),
        
        supabase
          .from('employee_notification_preferences')
          .select('*')
          .eq('employee_id', employeeData.id)
          .single(),
        
        supabase
          .from('employee_availability_preferences')
          .select('*')
          .eq('employee_id', employeeData.id)
          .single()
      ]);

      // Handle settings
      if (settingsResult.status === 'fulfilled' && !settingsResult.value.error) {
        setSettings(settingsResult.value.data);
      } else {
        setSettings([]);
      }

      // Handle notification preferences
      if (notificationResult.status === 'fulfilled' && !notificationResult.value.error) {
        setNotificationPreferences(notificationResult.value.data);
      } else {
        setNotificationPreferences(null);
      }

      // Handle availability preferences
      if (availabilityResult.status === 'fulfilled' && !availabilityResult.value.error) {
        setAvailabilityPreferences(availabilityResult.value.data);
      } else {
        setAvailabilityPreferences(null);
      }

    } catch (error) {
      console.error('Unexpected error fetching employee data:', error);
      setEmployee(null);
      setSettings(null);
      setNotificationPreferences(null);
      setAvailabilityPreferences(null);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isEmployee]);

  // Refresh functions
  const refreshEmployeeData = useCallback(() => fetchEmployeeData(), [fetchEmployeeData]);
  const refreshSettings = useCallback(async () => {
    if (!employee) return;

    try {
      const { data, error } = await supabase
        .from('employee_settings')
        .select('*')
        .eq('employee_id', employee.id);

      if (!error) {
        setSettings(data);
      }
    } catch (error) {
      console.error('Error refreshing settings:', error);
    }
  }, [employee]);

  // Setting management functions
  const updateSetting = useCallback(async (key: string, value: any, type: string = 'string') => {
    if (!employee) return;

    try {
      // Convert value to appropriate JSONB format
      let jsonValue: any;
      switch (type) {
        case 'boolean':
          jsonValue = Boolean(value);
          break;
        case 'number':
          jsonValue = Number(value);
          break;
        case 'object':
        case 'array':
          jsonValue = value;
          break;
        default:
          jsonValue = String(value);
          break;
      }

      const { error } = await supabase
        .from('employee_settings')
        .upsert({
          employee_id: employee.id,
          business_id: employee.business_id,
          setting_key: key,
          setting_value: jsonValue,
          setting_type: type,
        }, {
          onConflict: 'employee_id,setting_key'
        });

      if (error) throw error;

      // Update local state
      setSettings(prev => {
        if (!prev) return prev;
        
        const updatedSettings = [...prev];
        const existingIndex = updatedSettings.findIndex(s => s.setting_key === key);
        
        if (existingIndex >= 0) {
          updatedSettings[existingIndex] = {
            ...updatedSettings[existingIndex],
            setting_value: jsonValue,
            setting_type: type,
            updated_at: new Date().toISOString(),
          };
        } else {
          updatedSettings.push({
            id: crypto.randomUUID(),
            employee_id: employee.id,
            business_id: employee.business_id,
            setting_key: key,
            setting_value: jsonValue,
            setting_type: type,
            description: null,
            is_default: false,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });
        }
        
        return updatedSettings;
      });

    } catch (error) {
      console.error('Error updating setting:', error);
      throw error;
    }
  }, [employee]);

  const getSetting = useCallback((key: string, defaultValue: any = null) => {
    if (!settings) return defaultValue;
    
    const setting = settings.find(s => s.setting_key === key);
    if (!setting) return defaultValue;
    
    return setting.setting_value;
  }, [settings]);

  // Preference update functions
  const updateNotificationPreferences = useCallback(async (preferences: Partial<EmployeeNotificationPreferences>) => {
    if (!employee) return;

    try {
      const { error } = await supabase
        .from('employee_notification_preferences')
        .upsert({
          employee_id: employee.id,
          business_id: employee.business_id,
          ...preferences,
        }, {
          onConflict: 'employee_id'
        });

      if (error) throw error;

      // Update local state
      setNotificationPreferences(prev => prev ? { ...prev, ...preferences, updated_at: new Date().toISOString() } : null);

    } catch (error) {
      console.error('Error updating notification preferences:', error);
      throw error;
    }
  }, [employee]);

  const updateAvailabilityPreferences = useCallback(async (preferences: Partial<EmployeeAvailabilityPreferences>) => {
    if (!employee) return;

    try {
      const { error } = await supabase
        .from('employee_availability_preferences')
        .upsert({
          employee_id: employee.id,
          business_id: employee.business_id,
          ...preferences,
        }, {
          onConflict: 'employee_id'
        });

      if (error) throw error;

      // Update local state
      setAvailabilityPreferences(prev => prev ? { ...prev, ...preferences, updated_at: new Date().toISOString() } : null);

    } catch (error) {
      console.error('Error updating availability preferences:', error);
      throw error;
    }
  }, [employee]);

  // Effect to fetch data when user/employee status changes
  useEffect(() => {
    fetchEmployeeData();
  }, [fetchEmployeeData]);

  // Set up real-time subscriptions for settings changes
  useEffect(() => {
    if (!employee) return;

    const settingsSubscription = supabase
      .channel('employee_settings_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'employee_settings',
          filter: `employee_id=eq.${employee.id}`,
        },
        () => {
          // Skip refresh if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          refreshSettings();
        }
      )
      .subscribe();

    const notificationSubscription = supabase
      .channel('employee_notification_preferences_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'employee_notification_preferences',
          filter: `employee_id=eq.${employee.id}`,
        },
        (payload) => {
          // Skip update if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          if (payload.eventType !== 'DELETE') {
            setNotificationPreferences(payload.new as EmployeeNotificationPreferences);
          }
        }
      )
      .subscribe();

    const availabilitySubscription = supabase
      .channel('employee_availability_preferences_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'employee_availability_preferences',
          filter: `employee_id=eq.${employee.id}`,
        },
        (payload) => {
          // Skip update if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          if (payload.eventType !== 'DELETE') {
            setAvailabilityPreferences(payload.new as EmployeeAvailabilityPreferences);
          }
        }
      )
      .subscribe();

    return () => {
      settingsSubscription.unsubscribe();
      notificationSubscription.unsubscribe();
      availabilitySubscription.unsubscribe();
    };
  }, [employee, refreshSettings]);

  const contextValue: EmployeeContextType = {
    employee,
    loading,
    settings,
    notificationPreferences,
    availabilityPreferences,
    updateSetting,
    getSetting,
    updateNotificationPreferences,
    updateAvailabilityPreferences,
    refreshEmployeeData,
    refreshSettings,
  };

  return (
    <EmployeeContext.Provider value={contextValue}>
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployee = () => {
  const context = useContext(EmployeeContext);
  if (!context) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  return context;
};